import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import formService from '../../services/formService';

// Componentes
import BasicInfoStep from './FormBuilder/BasicInfoStep';
import SectionsAndCategoriesStep from './FormBuilder/SectionsAndCategoriesStep';
import QuestionsStep from './FormBuilder/QuestionsStep';
import ReviewStep from './FormBuilder/ReviewStep';

// ============================================================================
// TypeScript Interfaces - Updated for Sections + Categories architecture
// ============================================================================

export interface FormData {
  id?: number;
  nombre: string;
  descripcion: string;
  version?: number;
  activo: boolean;
  es_template: boolean;
}

// NEW: Section for physical/logical navigation
export interface Section {
  id?: number;
  form_id?: number;
  name: string;
  description?: string;
  order: number;
  color?: string;
  icon?: string;
  question_count?: number;
  created_at?: string;
}

// UPDATED: Category now only for conceptual analysis
export interface Category {
  id?: number;
  nombre: string;
  descripcion?: string;
  color?: string;
  icono?: string;
  total_weighting?: number;
  created_at?: string;
}

// UPDATED: Question now belongs to section + category
export interface Question {
  id?: number;
  section_id?: number; // NEW: For navigation
  category_id?: number; // For analysis/reporting
  texto: string;
  descripcion?: string;
  tipo: 'yes_no' | 'scale' | 'text' | 'number' | 'select' | 'multiple' | 'date' | 'time';
  obligatoria: boolean;
  ponderacion: number;
  orden: number;
  opciones?: Array<{ value: string; label: string; puntos?: number }>;
  config?: {
    min?: number;
    max?: number;
    paso?: number;
    unidad?: string;
  };
  puntos_si?: number;
  puntos_no?: number;
  puntos_na?: number;
  permite_evidencia: boolean;
  permite_comentario: boolean;
  section_name?: string; // For display
  category_name?: string; // For display
}

export interface FormBuilderData {
  formInfo: FormData;
  sections: Section[]; // NEW: Replaces categories for navigation
  categories: Category[]; // NEW: Pool of categories for analysis
  questions: Question[];
}

const steps = ['Información Básica', 'Secciones y Categorías', 'Preguntas', 'Revisión'];

const FormBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Estados principales
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Datos del formulario
  const [formData, setFormData] = useState<FormBuilderData>({
    formInfo: {
      nombre: '',
      descripcion: '',
      activo: true,
      es_template: false
    },
    sections: [], // NEW: Physical sections for navigation
    categories: [], // NEW: Conceptual categories for analysis
    questions: []
  });

  // ✅ Snapshot de datos originales para detectar eliminaciones
  const [originalData, setOriginalData] = useState<{
    sections: number[];
    categories: number[];
    questions: number[];
  }>({ sections: [], categories: [], questions: [] });

  // Cargar formulario existente si estamos en modo edición
  useEffect(() => {
    if (isEditMode && id) {
      loadFormData(parseInt(id));
    }
  }, [id, isEditMode]);

  const loadFormData = async (formId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Load form information
      const formResponse = await formService.getById(Number(formId));
      console.log('Form response:', formResponse);
      const formInfo = formResponse.data || formResponse;

      // Load sections (NEW: for navigation)
      const sectionsResponse = await formService.getSections(Number(formId));
      console.log('Sections response:', sectionsResponse);
      const sectionsData = sectionsResponse.sections || sectionsResponse.data || sectionsResponse;
      const sections = Array.isArray(sectionsData) ? sectionsData : [];

      // Load categories (for analysis)
      const categoriesResponse = await formService.getCategories(Number(formId));
      console.log('Categories response:', categoriesResponse);
      const categoriesData = categoriesResponse.data || categoriesResponse;
      const categories = Array.isArray(categoriesData) ? categoriesData : [];

      // Load questions (now with section_id)
      const questionsResponse = await formService.getQuestions(Number(formId));
      console.log('Questions response:', questionsResponse);
      const questionsData = questionsResponse.data || questionsResponse;
      const questions = Array.isArray(questionsData) ? questionsData : [];
      console.log('Questions array:', questions);
      console.log('Questions length:', questions.length);

      const formDataToSet = {
        formInfo: {
          id: formInfo.id,
          nombre: formInfo.name,
          descripcion: formInfo.description || '',
          version: formInfo.version,
          activo: formInfo.active,
          es_template: formInfo.is_template
        },
        sections: sections.map((section: any) => ({
          id: section.id,
          form_id: section.form_id,
          name: section.name,
          description: section.description || '',
          order: section.order,
          color: section.color || '#2196F3',
          icon: section.icon,
          question_count: section.question_count || 0
        })),
        categories: categories.map((cat: any) => ({
          id: cat.id,
          nombre: cat.name,
          descripcion: cat.description || '',
          color: cat.color || '#2196F3',
          icono: cat.icon,
          total_weighting: parseFloat(cat.total_weighting || 0)
        })),
        questions: questions.map((q: any) => ({
          id: q.id,
          section_id: q.section_id, // NEW: Physical section
          category_id: q.category_id, // Conceptual category
          texto: q.text,
          descripcion: q.description || '',
          tipo: q.type,
          obligatoria: q.required,
          ponderacion: parseFloat(q.weighting),
          orden: q.order,
          opciones: q.options,
          config: q.config,
          puntos_si: q.points_yes ?? 0,
          puntos_no: q.points_no ?? 0,
          puntos_na: q.points_na ?? 0,
          permite_evidencia: q.allows_evidence,
          permite_comentario: q.allows_comment,
          section_name: q.section_name,
          category_name: q.category_name
        }))
      };

      setFormData(formDataToSet);

      // ✅ Guardar snapshot de IDs originales
      setOriginalData({
        sections: sections.map((s: any) => s.id).filter((id: number) => id > 0),
        categories: categories.map((c: any) => c.id).filter((id: number) => id > 0),
        questions: questions.map((q: any) => q.id).filter((id: number) => id > 0)
      });

      console.log('FormData set with questions:', questions.length);
      console.log('📸 Original IDs snapshot:', {
        sections: sections.length,
        categories: categories.length,
        questions: questions.length
      });

    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar el formulario');
      console.error('Error loading form:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Información básica
        if (!formData.formInfo.nombre.trim()) {
          setError('El nombre del formulario es requerido');
          return false;
        }
        break;
      case 1: // Secciones y Categorías
        if (formData.sections.length === 0) {
          setError('Debe agregar al menos una sección');
          return false;
        }
        if (formData.categories.length === 0) {
          setError('Debe agregar al menos una categoría');
          return false;
        }
        break;
      case 2: // Preguntas
        if (formData.questions.length === 0) {
          setError('Debe agregar al menos una pregunta');
          return false;
        }
        // Validar ponderación por categoría
        for (const category of formData.categories) {
          // Filtrar preguntas que pertenecen a esta categoría
          const categoryQuestions = formData.questions.filter(q => 
            q.category_id === category.id
          );
          const totalPonderacion = categoryQuestions.reduce((sum, q) => sum + q.ponderacion, 0);
          
          console.log(`Validación Categoría "${category.nombre}" (id: ${category.id}):`, {
            questionsFound: categoryQuestions.length,
            questions: categoryQuestions.map(q => ({ 
              section_id: q.section_id,
              category_id: q.category_id,
              ponderacion: q.ponderacion
            })),
            totalPonderacion
          });
          
          if (categoryQuestions.length > 0 && Math.abs(totalPonderacion - 100) > 0.01) {
            setError(`La ponderación de la categoría "${category.nombre}" debe sumar 100% (actual: ${totalPonderacion.toFixed(2)}%)`);
            return false;
          }
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isEditMode && formData.formInfo.id) {
        await updateForm();
      } else {
        await createForm();
      }

      setSuccessMessage('Formulario guardado exitosamente');
      setSaveDialogOpen(false);
      
      setTimeout(() => {
        navigate('/forms');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el formulario');
      console.error('Error saving form:', err);
    } finally {
      setLoading(false);
    }
  };

  const createForm = async () => {
    // 1. Crear formulario
    const formResponse = await formService.create({
      name: formData.formInfo.nombre,
      description: formData.formInfo.descripcion
    });

    const formId = formResponse.data?.id || formResponse.id;

    // 2. Crear secciones
    const sectionIdMap = new Map<number, number>(); // tempId -> realId
    
    for (const section of formData.sections) {
      const sectionResponse = await formService.createSection(formId, {
        name: section.name,
        description: section.description,
        order: section.order,
        color: section.color
      });
      
      console.log('[DEBUG] Section response:', sectionResponse);
      console.log('[DEBUG] sectionResponse.data:', sectionResponse.data);
      console.log('[DEBUG] sectionResponse.section:', sectionResponse.section);
      console.log('[DEBUG] sectionResponse.id:', sectionResponse.id);
      
      const realId = sectionResponse.section?.id || sectionResponse.data?.id || sectionResponse.id;
      console.log('[DEBUG] Extracted realId:', realId);
      console.log('[DEBUG] Mapping section.id', section.id, '-> realId', realId);
      
      sectionIdMap.set(section.id || section.order, realId);
    }

    // 3. Crear categorías
    const categoryIdMap = new Map<number, number>(); // tempId -> realId
    
    for (const category of formData.categories) {
      const catResponse = await formService.createCategory(formId, {
        name: category.nombre,
        description: category.descripcion,
        color: category.color
      });
      
      const realId = catResponse.data?.id || catResponse.id;
      categoryIdMap.set(category.id || Date.now(), realId);
    }

    // 4. Crear preguntas
    console.log('[DEBUG] Total questions to create:', formData.questions.length);
    console.log('[DEBUG] sectionIdMap entries:', sectionIdMap.size, Array.from(sectionIdMap.entries()));
    console.log('[DEBUG] categoryIdMap entries:', categoryIdMap.size, Array.from(categoryIdMap.entries()));
    console.log('[DEBUG] All sections:', formData.sections.map(s => ({ id: s.id, order: s.order, name: s.name })));
    
    for (const question of formData.questions) {
      console.log('[DEBUG] Looking up question.section_id:', question.section_id, 'type:', typeof question.section_id);
      console.log('[DEBUG] Map has key?', question.section_id ? sectionIdMap.has(question.section_id) : false);
      
      const realSectionId = sectionIdMap.get(question.section_id || 0);
      const realCategoryId = categoryIdMap.get(question.category_id || 0);

      console.log('[DEBUG] Question:', {
        texto: question.texto,
        section_id: question.section_id,
        category_id: question.category_id,
        realSectionId,
        realCategoryId
      });

      if (realSectionId && realCategoryId) {
        // Convertir opciones al formato esperado por el backend
        const opciones = Array.isArray(question.opciones)
          ? question.opciones.map(opt => 
              typeof opt === 'string' ? opt : opt.label || opt.value
            )
          : undefined;
        
        await formService.createQuestion(formId, realSectionId, {
          text: question.texto,
          category_id: realCategoryId,
          type: question.tipo as any,
          required: question.obligatoria,
          weighting: question.ponderacion,
          options: opciones,
          order: question.orden,
          allows_evidence: question.permite_evidencia,
          allows_comment: question.permite_comentario
        });
      }
    }
  };

  const updateForm = async () => {
    const formId = formData.formInfo.id!;

    console.log('=== BULK UPDATE FORM ===');
    console.log('FormData:', formData);

    // ===== PREPARAR DATOS PARA BULK UPDATE =====

    // 1. Información del formulario
    const formInfo = {
      name: formData.formInfo.nombre,
      description: formData.formInfo.descripcion,
      active: formData.formInfo.activo,
      is_template: formData.formInfo.es_template
    };

    // 2. Clasificar secciones en: crear, actualizar, eliminar
    const existingSections = formData.sections.filter(s => s.id && s.id > 0);
    const newSections = formData.sections.filter(s => !s.id || s.id < 0);
    const currentSectionIds = existingSections.map(s => s.id!);
    const deletedSectionIds = originalData.sections.filter(id => !currentSectionIds.includes(id));

    const sections = {
      toUpdate: existingSections.map(s => ({
        id: s.id!,
        name: s.name,
        description: s.description,
        order: s.order,
        color: s.color || '#2196F3'
      })),
      toCreate: newSections.map(s => ({
        tempId: s.id || s.order, // ID temporal para mapeo
        name: s.name,
        description: s.description,
        order: s.order,
        color: s.color || '#2196F3'
      })),
      toDelete: deletedSectionIds
    };

    // 3. Clasificar categorías en: crear, actualizar, eliminar
    const existingCategories = formData.categories.filter(c => c.id && c.id > 0);
    const newCategories = formData.categories.filter(c => !c.id || c.id < 0);
    const currentCategoryIds = existingCategories.map(c => c.id!);
    const deletedCategoryIds = originalData.categories.filter(id => !currentCategoryIds.includes(id));

    const categories = {
      toUpdate: existingCategories.map(c => ({
        id: c.id!,
        name: c.nombre,
        description: c.descripcion,
        color: c.color || '#4CAF50'
      })),
      toCreate: newCategories.map(c => ({
        tempId: c.id || Date.now(), // ID temporal para mapeo
        name: c.nombre,
        description: c.descripcion,
        color: c.color || '#4CAF50'
      })),
      toDelete: deletedCategoryIds
    };

    // 4. Clasificar preguntas en: crear, actualizar, eliminar
    const existingQuestions = formData.questions.filter(q => q.id && q.id > 0);
    const newQuestions = formData.questions.filter(q => !q.id || q.id < 0);
    const currentQuestionIds = existingQuestions.map(q => q.id!);
    const deletedQuestionIds = originalData.questions.filter(id => !currentQuestionIds.includes(id));

    const questions = {
      toUpdate: existingQuestions.map(q => {
        const opciones = Array.isArray(q.opciones) 
          ? q.opciones.map(opt => typeof opt === 'string' ? opt : opt.label || opt.value)
          : undefined;

        return {
          id: q.id!,
          text: q.texto,
          description: q.descripcion,
          type: q.tipo,
          required: q.obligatoria,
          weighting: q.ponderacion,
          order: q.orden,
          options: opciones,
          allows_evidence: q.permite_evidencia,
          allows_comment: q.permite_comentario,
          section_id: q.section_id,
          category_id: q.category_id
        };
      }),
      toCreate: newQuestions.map(q => {
        const opciones = Array.isArray(q.opciones)
          ? q.opciones.map(opt => typeof opt === 'string' ? opt : opt.label || opt.value)
          : undefined;

        return {
          text: q.texto,
          description: q.descripcion,
          type: q.tipo,
          required: q.obligatoria,
          weighting: q.ponderacion,
          order: q.orden,
          options: opciones,
          allows_evidence: q.permite_evidencia,
          allows_comment: q.permite_comentario,
          section_id: q.section_id, // Puede ser ID temporal
          category_id: q.category_id // Puede ser ID temporal
        };
      }),
      toDelete: deletedQuestionIds
    };

    console.log('📦 Payload bulk:', {
      formInfo,
      sections,
      categories,
      questions
    });
    
    console.log('📦 Payload details:');
    console.log('  - sections.toUpdate:', sections.toUpdate.length, 'items');
    console.log('  - sections.toCreate:', sections.toCreate.length, 'items');
    console.log('  - sections.toDelete:', sections.toDelete.length, 'items', sections.toDelete);
    console.log('  - categories.toUpdate:', categories.toUpdate.length, 'items');
    console.log('  - categories.toCreate:', categories.toCreate.length, 'items');
    console.log('  - categories.toDelete:', categories.toDelete.length, 'items', categories.toDelete);
    console.log('  - questions.toUpdate:', questions.toUpdate.length, 'items');
    console.log('  - questions.toCreate:', questions.toCreate.length, 'items');
    console.log('  - questions.toDelete:', questions.toDelete.length, 'items', questions.toDelete);

    // ===== ENVIAR TODO EN UNA SOLA PETICIÓN =====
    const response = await formService.bulkUpdate(formId, {
      formInfo,
      sections,
      categories,
      questions
    });

    console.log('✅ Bulk update response:', response);

    // Actualizar IDs temporales con IDs reales si es necesario
    if (response.sectionIdMap) {
      console.log('🔄 Section ID mapping:', response.sectionIdMap);
    }
    if (response.categoryIdMap) {
      console.log('🔄 Category ID mapping:', response.categoryIdMap);
    }
  };

  const handlePreview = () => {
    if (formData.formInfo.id) {
      window.open(`/forms/${formData.formInfo.id}/preview`, '_blank');
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <BasicInfoStep
            formInfo={formData.formInfo}
            onChange={(info) => setFormData({ ...formData, formInfo: info })}
          />
        );
      case 1:
        return (
          <SectionsAndCategoriesStep
            sections={formData.sections}
            categories={formData.categories}
            onSectionsChange={(sections: Section[]) => setFormData({ ...formData, sections })}
            onCategoriesChange={(categories: Category[]) => setFormData({ ...formData, categories })}
          />
        );
      case 2:
        return (
          <QuestionsStep
            sections={formData.sections}
            categories={formData.categories}
            questions={formData.questions}
            onChange={(questions: Question[]) => setFormData({ ...formData, questions })}
          />
        );
      case 3:
        return (
          <ReviewStep
            formData={formData}
          />
        );
      default:
        return null;
    }
  };

  if (loading && isEditMode && !formData.formInfo.nombre) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/forms')}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Editar Formulario' : 'Crear Nuevo Formulario'}
          </Typography>
        </Box>
        <Box>
          {isEditMode && (
            <Button
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              sx={{ mr: 1 }}
            >
              Vista Previa
            </Button>
          )}
        </Box>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Contenido del paso actual */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {renderStepContent()}
      </Paper>

      {/* Botones de navegación */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Anterior
          </Button>
          
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={() => setSaveDialogOpen(true)}
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Formulario'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Siguiente
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Diálogo de confirmación */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Confirmar Guardado</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea guardar el formulario "{formData.formInfo.nombre}"?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            <strong>Resumen:</strong>
            <br />
            • Categorías: {formData.categories.length}
            <br />
            • Preguntas: {formData.questions.length}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilder;
